import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { CandidateSuccessModal, ChoiceChip, FormDialog } from '@components';

import { Box, FormLabel, TextField } from '@mui/material';

import { candidateSubtitle, isStatusLoading, toggle } from '@utils';

import {
  candidateDetailsSelectors,
  sendCandidateQuestion,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import {
  CandidateDetailsAction,
  MORE_INFO_TOPICS,
  SearchParams,
} from '@constants';

export const CandidateAskQuestionModal = () => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const { sendCandidateQuestionAPIStatus, writeError, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );
  const candidateDetailsAction = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsAction,
  );

  const handleModalClose = () => {
    dispatch(setCandidateDetailsAction(null));
    setSearchParams(
      (_searchParams) => {
        _searchParams.delete(SearchParams.ACTION);
        return _searchParams;
      },
      { replace: true },
    );
  };

  const onSubmitQuestion = async (text: string) => {
    if (!data?.id) return false;

    const res = await dispatch(
      sendCandidateQuestion({ candidateId: data.id, text }),
    );

    return sendCandidateQuestion.fulfilled.match(res);
  };

  return (
    <AskQuestionDialog
      subject={
        data && {
          name: [data.profile?.firstName, data.profile?.lastName]
            .filter(Boolean)
            .join(' '),
          subtitle: candidateSubtitle(data),
        }
      }
      isOpen={candidateDetailsAction === CandidateDetailsAction.ASK_QUESTION}
      isLoading={isStatusLoading(sendCandidateQuestionAPIStatus)}
      error={writeError}
      onClose={handleModalClose}
      onSubmit={onSubmitQuestion}
    />
  );
};

interface IAskQuestionDialogProps {
  subject: { name: string; subtitle: string } | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (text: string) => Promise<boolean>;
}

export const AskQuestionDialog: React.FC<IAskQuestionDialogProps> = ({
  subject,
  isOpen,
  isLoading,
  error,
  onClose,
  onSubmit,
}) => {
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);

  const [questionText, setQuestionText] = useState('');
  const [topics, setTopics] = useState<string[]>([]);

  const onSubmitQuestion = async () => {
    const isSent = await onSubmit(
      [topics.length ? `Topics: ${topics.join(', ')}` : '', questionText]
        .filter(Boolean)
        .join('\n\n'),
    );

    if (isSent) {
      onClose();
      setSuccessModalOpen(true);
    }
  };

  if (!subject) return null;

  const header = { label: subject.name, subLabel: subject.subtitle };

  return (
    <>
      <FormDialog
        isOpen={isOpen}
        onClose={onClose}
        {...header}
        onSubmit={onSubmitQuestion}
        isLoading={isLoading}
        submitDisabled={!questionText.trim()}
        hint={questionText.trim() ? '' : 'Write your question to submit.'}
        error={error}
      >
        <Box my={2}>
          <FormLabel sx={{ fontSize: '0.75rem' }}>
            What would help you decide?
          </FormLabel>
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={1} mb={2}>
            {MORE_INFO_TOPICS.map((topic) => {
              const selected = topics.includes(topic);

              return (
                <ChoiceChip
                  key={topic}
                  selected={selected}
                  onClick={() => setTopics(toggle(topics, topic))}
                >
                  {topic}
                </ChoiceChip>
              );
            })}
          </Box>
          <TextField
            label="Question"
            placeholder="Anything you'd wanna know about the candidate before interview"
            variant="outlined"
            fullWidth
            value={questionText}
            required
            onChange={(e) => setQuestionText(e.target.value)}
            disabled={isLoading}
            multiline
            minRows={3}
          />
        </Box>
      </FormDialog>
      <CandidateSuccessModal
        header={header}
        isOpen={isSuccessModalOpen}
        isPassed
        label="Thanks, we'll get back to you"
        subLabel="It may take some time to gather details from the candidate"
        handleClose={() => setSuccessModalOpen(false)}
      />
    </>
  );
};
